import { Alarm, Clear } from '@mui/icons-material';
import { FormControl, FormGroup, FormLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useFormFields } from 'libs/hooksLib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

import ActionButton from 'common/components/ActionButton';
import useFlexBoxStyles from 'common/flexBox';
import LayoutSubForm from 'common/layouts/LayoutSubForm';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    padding0: {
        '& .MuiInputBase-root': {
            padding: 0,
        },
    },
    eventInputSelector: {
        margin: theme.spacing(0.5),
        backgroundColor: 'none',
        '& > :first-of-type': {
            height: '30px',
            fontSize: theme.typography.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            width: 'auto',
            backgroundColor: theme.palette.secondary.light,
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
            overflow: 'hidden',
        },
        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.default.main,
        },
    },
}));

const EventFormReminders = (props) => {
    const { eventReminders, onConfirm, close } = props;
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const [fields, handleFieldChange] = useFormFields({
        time: 0,
        unit: 'h',
    });

    const [reminders, setReminders] = useState(eventReminders || []);

    const done = (event) => {
        event.preventDefault();
        onConfirm(reminders);
        close();
    };

    const handleAddReminderSubmit = (e) => {
        e.preventDefault();
        setReminders([...reminders, fields]);
    };

    const deleteReminder = (index) => {
        if (index >= 0 && index < reminders.length) {
            setReminders([...reminders.slice(0, index), ...reminders.slice(index + 1, reminders.length)]);
        }
    };

    return (
        <LayoutSubForm sectionName={'Reminders'} close={close} done={done} showCloseButton={true}>
            <ValidatorForm
                onSubmit={handleAddReminderSubmit}
                onError={() => {}}
                className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
            >
                <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.vH100)}>
                    <FormGroup>
                        <FormLabel>{t('event.eventFormReminders.addReminder')}</FormLabel>
                        <TextValidator
                            name="time"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            type={'number'}
                            value={fields.time}
                            onChange={handleFieldChange}
                            className={classes.padding0}
                            validators={[`minNumber:0`, 'required']}
                            errorMessages={[t('event.eventFormReminders.minValue'), t('app.common.required')]}
                            InputProps={{
                                endAdornment: (
                                    <FormGroup>
                                        <FormControl>
                                            <TextField
                                                name="unit"
                                                select
                                                className={classes.eventInputSelector}
                                                value={fields.unit}
                                                onChange={handleFieldChange}
                                                variant="outlined"
                                                size="small"
                                            >
                                                <MenuItem key="hour" value="h">
                                                    {t('event.eventFormReminders.hours')}
                                                </MenuItem>
                                                <MenuItem key="minute" value="m">
                                                    {t('event.eventFormReminders.minutes')}
                                                </MenuItem>
                                                <MenuItem key="second" value="s">
                                                    {t('event.eventFormReminders.seconds')}
                                                </MenuItem>
                                            </TextField>
                                        </FormControl>
                                    </FormGroup>
                                ),
                            }}
                        />
                    </FormGroup>
                    <ActionButton color="primary" type="submit" autoFocus fitWidth>
                        {t('event.eventFormReminders.add')}
                    </ActionButton>
                    <Stack direction="column" spacing={2}>
                        {reminders?.map((reminder, index) => {
                            return (
                                <Stack
                                    direction="row"
                                    justifyContent="space-evenly"
                                    alignItems="center"
                                    key={`reminder-${index}`}
                                >
                                    <Alarm />
                                    <Typography variant="subtitle2">{`${reminder.time}${reminder.unit} before`}</Typography>
                                    <Clear onClick={() => deleteReminder(index)} />
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
            </ValidatorForm>
        </LayoutSubForm>
    );
};

export default EventFormReminders;
